import {
  ApolloClient,
  ApolloLink,
  from,
  InMemoryCache,
  NormalizedCacheObject,
  FieldMergeFunction,
  Reference,
} from '@apollo/client'

import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries'
import type { Fetch } from '@moonpig/web-core-fetch'
import {
  createProxyApiUrl,
  createSha256Hash,
  getApiUrl,
} from '@moonpig/web-core-utils'
import {
  createContextLink,
  createErrorLink,
  createLogLink,
  createHttpLink,
  createOpenTelemetryLink,
} from './links'
import { GraphQLOptions } from './types'

export const createClient = (
  options: GraphQLOptions,
  isServer: boolean,
  initialState: NormalizedCacheObject,
  fetch: Fetch,
) => {
  const { headers } = options
  const graphqlGatewayUrl = `${getApiUrl()}/graphql`
  const uri = createProxyApiUrl(graphqlGatewayUrl)

  const logLink = createLogLink()
  const contextLink = createContextLink({ headers })
  const errorLink = createErrorLink()
  const openTelemetryLink = createOpenTelemetryLink()

  /* istanbul ignore next */
  const persistedQueryLink = options.usePersistedQueries
    ? [
        new ApolloLink((operation, forward) => forward(operation)).split(
          op => JSON.stringify(op.variables).length > 4096,
          createPersistedQueryLink({
            useGETForHashedQueries: false,
            sha256: (query: string) => createSha256Hash(query),
          }),
          createPersistedQueryLink({
            useGETForHashedQueries: true,
            sha256: (query: string) => createSha256Hash(query),
          }),
        ),
      ]
    : /* istanbul ignore next */ []

  const links = [
    ...persistedQueryLink,
    logLink,
    errorLink,
    openTelemetryLink,
    contextLink,
  ]

  links.push(createHttpLink({ uri, fetch }))

  const client = new ApolloClient({
    connectToDevTools: !isServer,
    ssrMode: isServer,
    link: from(links),
    cache: new InMemoryCache({
      typePolicies: {
        Me: {
          keyFields: [],
        },
        Basket: {
          fields: {
            lineItems: {
              merge: ((existing = [], incoming, { readField }) => {
                const existingLineItems = new Map<
                  string,
                  { item: Reference; index: number }
                >()

                existing.forEach((item: Reference, index: number) => {
                  const id = readField('id', item)
                  if (typeof id === 'string') {
                    existingLineItems.set(id, { item, index })
                  }
                })

                if (!Array.isArray(incoming)) {
                  return incoming
                }

                return incoming.map(incomingItem => {
                  const id = readField('id', incomingItem)
                  if (typeof id === 'string' && existingLineItems.has(id)) {
                    const existingData = existingLineItems.get(id)
                    // istanbul ignore next
                    return existingData ? existingData.item : incomingItem
                  }
                  return incomingItem
                })
              }) satisfies FieldMergeFunction<Reference[], Reference[]>,
            },
          },
        },
      },
    }).restore(initialState),
    assumeImmutableResults: true,
  })

  return client
}
